import { useEffect, useState } from "react";
import { Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PLAN_NAMES = {
  "basic-broadcaster": "Basic Broadcaster", "media-ministry-hub": "Media Ministry Hub",
  "premium-services": "Premium Services", "ascend-ultra": "Ascend Ultra",
};

export const SendInvoiceDialog = ({ user, onClose }) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const month = new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const plan = PLAN_NAMES[user.plan];
    setDescription(plan ? `StreamWorship ${plan} — ${month}` : `StreamWorship services — ${month}`);
    if (user.pricing_override === "custom" && user.custom_price) { setAmount(String(user.custom_price)); return; }
    if (!plan) return;
    api.get("/admin/billing/prices").then((r) => setAmount(String(r.data.prices[user.plan] || ""))).catch(() => {});
  }, [user]);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/users/${user.user_id}/invoice`, { amount: Number(amount), description });
      toast.success(`Invoice sent to ${r.data.email}`, { description: `$${r.data.amount.toFixed(2)} due on receipt${r.data.number ? ` · ${r.data.number}` : ""}` });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send invoice"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="invoice-dialog" className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><Receipt className="h-5 w-5 text-amber-400" /> Send Invoice</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">Stripe will email an invoice to <span className="text-gray-200 font-medium">{user.email}</span>, due on receipt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Amount (USD)</Label><Input data-testid="invoice-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="bg-black/30 border-gray-800" /></div>
          <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Description</Label><Input data-testid="invoice-description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} className="bg-black/30 border-gray-800" /></div>
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button data-testid="invoice-send" onClick={send} disabled={sending || !(Number(amount) >= 1) || !description.trim()} className="inline-flex items-center gap-2 rounded-lg gold-gradient px-4 py-2 text-sm font-semibold text-black hover:brightness-110 disabled:opacity-50">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />} Send Invoice
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
